<template>
    <div>
        <v-card :loading="loading">
            <v-card-title class="text-h5">Generate Daily Report</v-card-title>
            <v-card-text>
                <div class="my-5 mb-n4">
                    <v-row>
                        <v-col cols="6">
                            <v-menu
                                v-model="menuStartDate"
                                :close-on-content-click="false"
                                transition="scale-transition"
                                offset-y
                            >
                                <template v-slot:activator="{ on, attrs }">
                                    <v-text-field
                                        v-model="startDate"
                                        label="Start Date"
                                        prepend-icon="mdi-calendar"
                                        readonly
                                        v-bind="attrs"
                                        v-on="on"
                                        clearable
                                        @click:clear="startDate = null"
                                        :rules="[rules.required]"
                                    />
                                </template>

                                <v-date-picker
                                    v-model="startDate"
                                    :max="
                                        new Date().toISOString().substr(0, 10)
                                    "
                                    @input="selectStartDate"
                                />
                            </v-menu>
                        </v-col>

                        <v-col cols="6">
                            <v-menu
                                v-model="menuEndDate"
                                :close-on-content-click="false"
                                transition="scale-transition"
                                offset-y
                            >
                                <template v-slot:activator="{ on, attrs }">
                                    <v-text-field
                                        v-model="endDate"
                                        label="End Date"
                                        prepend-icon="mdi-calendar"
                                        readonly
                                        v-bind="attrs"
                                        v-on="on"
                                        clearable
                                        @click:clear="endDate = null"
                                        :rules="[
                                            rules.required,

                                            validateMaxRange,
                                        ]"
                                    />
                                </template>

                                <v-date-picker
                                    v-model="endDate"
                                    :min="minEndDate"
                                    :max="maxEndDate"
                                    @input="selectEndDate"
                                />
                            </v-menu>
                        </v-col>
                    </v-row>
                </div>
            </v-card-text>

            <v-card-actions>
                <v-btn text color="secondary" @click="closeWoReport">
                    Close
                </v-btn>

                <v-spacer></v-spacer>

                <v-btn
                    text
                    color="primary"
                    :disabled="!startDate || !endDate || loading"
                    @click="getDailyWorkOrders()"
                >
                    Generate
                </v-btn>
            </v-card-actions>
        </v-card>
    </div>
</template>

<script>
import { mapMutations } from 'vuex'
import moment from 'moment'
import API from '@/services/api'
import * as XLSX from 'xlsx/xlsx.mjs'

export default {
    name: 'WorkOrderReport',

    data: () => ({
        loading: false,
        rules: {
            required: v => !!v || 'Required',
        },
        startDate: null,
        endDate: null,
        menuStartDate: false,
        menuEndDate: false,
    }),

    computed: {
        minEndDate() {
            return this.startDate || null
        },

        maxEndDate() {
            if (!this.startDate) return new Date().toISOString().substr(0, 10)
            const max = new Date(this.startDate)
            max.setMonth(max.getMonth() + 3)

            const today = new Date()
            return (max > today ? today : max).toISOString().substr(0, 10)
        },
    },

    methods: {
        ...mapMutations(['setErrorItems']),
        // report generation
        async getDailyWorkOrders() {
            try {
                this.loading = true

                let workOrders = await API.getDailyWorkOrders({
                    startDate: this.startDate,
                    endDate: this.endDate,
                })

                const daysInRange = this.getDaysInRange(
                    this.startDate,
                    this.endDate
                )

                let enteredWO = {}
                let openWO = {}
                let openKg = {}
                let finishedWO = {}
                let enteredKG = {}
                let finishedKG = {}

                if (workOrders.length > 0) {
                    for (let wo of workOrders) {
                        // Dates ---
                        // get current date
                        const currentDay = moment(new Date()).format(
                            'YYYY-MM-DD'
                        )
                        // set entered work orders and open work orders
                        const approvedDate =
                            wo.approvedDate?._seconds ||
                            wo.approvedDate?.seconds
                                ? this.formatDate(
                                      wo.approvedDate?._seconds ||
                                          wo.approvedDate?.seconds
                                  )
                                : null
                        // get finish date
                        const finishDate =
                            wo.finishDate?._seconds || wo.finishDate?.seconds
                                ? this.formatDate(
                                      wo.finishDate?._seconds ||
                                          wo.finishDate?.seconds
                                  )
                                : null
                        // get production start date
                        const productionStart =
                            wo.productionStart?._seconds ||
                            wo.productionStart?.seconds
                                ? this.formatDate(
                                      wo.productionStart?._seconds ||
                                          wo.productionStart?.seconds
                                  )
                                : null
                        // initialize range days for open KG
                        let rangeDays = []

                        // Get entered WO and Open WO
                        if (
                            approvedDate &&
                            daysInRange.includes(approvedDate)
                        ) {
                            // Get Entered WO ---
                            // initialize if not exist
                            if (!enteredWO[approvedDate]) {
                                enteredWO[approvedDate] = 0
                            }
                            // sum Entered WO
                            enteredWO[approvedDate] += 1

                            // Get Open WO ---
                            // create range of time
                            let daysFromApprovedToFinished = []
                            if (finishDate) {
                                // Use finished date if the work order has been finished
                                daysFromApprovedToFinished = this.getDaysInRange(
                                    approvedDate,
                                    finishDate
                                )
                            } else {
                                // Use current date if the work order has not been finished
                                daysFromApprovedToFinished = this.getDaysInRange(
                                    approvedDate,
                                    currentDay
                                )
                            }

                            // If the range from approved date to finish date overlaps with the given days,
                            // count the work order as open.
                            if (daysFromApprovedToFinished.length > 0) {
                                daysFromApprovedToFinished.forEach(rangeDay => {
                                    if (
                                        daysInRange.includes(rangeDay) &&
                                        rangeDay != finishDate
                                    ) {
                                        // initialize if not exist
                                        if (!openWO[rangeDay]) {
                                            openWO[rangeDay] = 0
                                        }
                                        // sum to Open WO
                                        openWO[rangeDay] += 1
                                        // save range day
                                        rangeDays.push(rangeDay)
                                    }
                                })
                            }
                        }

                        // Get Finished WO ---
                        if (finishDate && daysInRange.includes(finishDate)) {
                            // initialize if not exist
                            if (!finishedWO[finishDate]) {
                                finishedWO[finishDate] = 0
                            }
                            // sum Finished Wo
                            finishedWO[finishDate] += 1
                        }

                        // set entered kg, finished kg and open kg
                        if (
                            wo.partNumbers &&
                            Object.values(wo.partNumbers)?.length > 0
                        ) {
                            Object.values(wo.partNumbers).forEach(
                                partNumber => {
                                    if (productionStart) {
                                        // initialize if not exist
                                        if (!enteredKG[productionStart]) {
                                            enteredKG[productionStart] = 0
                                        }
                                        const kg =
                                            (partNumber.quantity || 0) *
                                            (partNumber.mass || 0)
                                        // set entered kg
                                        enteredKG[productionStart] += kg
                                        // set finished kg (kg for finished WOS)
                                        if (finishDate) {
                                            if (!finishedKG[finishDate]) {
                                                finishedKG[finishDate] = 0
                                            }
                                            finishedKG[finishDate] += kg
                                        }
                                        // sum open kg ( kg of open work orders )
                                        if (rangeDays.length > 0) {
                                            for (let rangeDay of rangeDays) {
                                                if (
                                                    daysInRange.includes(
                                                        rangeDay
                                                    )
                                                ) {
                                                    // initialize if not exist
                                                    if (!openKg[rangeDay]) {
                                                        openKg[rangeDay] = 0
                                                    }
                                                    openKg[rangeDay] += kg
                                                }
                                            }
                                        }
                                    }
                                }
                            )
                        }
                    }
                }

                // Create rows
                const rows = daysInRange.map(date => ({
                    Dates: date,
                    'Entered WO': enteredWO[date] ?? 0,
                    'Entered KG': enteredKG[date] ?? 0,
                    'Finished WO': finishedWO[date] ?? 0,
                    'Finished KG': finishedKG[date] ?? 0,
                    'Open WO': openWO[date] ?? 0,
                    'Open KG': openKg[date] ?? 0,
                }))

                const worksheet = XLSX.utils.json_to_sheet(rows, {
                    header: [
                        'Dates',
                        'Entered WO',
                        'Entered KG',
                        'Finished WO',
                        'Finished KG',
                        'Open WO',
                        'Open KG',
                    ],
                })

                const workbook = XLSX.utils.book_new()
                XLSX.utils.book_append_sheet(
                    workbook,
                    worksheet,
                    'WO Daily Report'
                )
                XLSX.writeFile(workbook, 'WO-daily-report.xlsx')
            } catch (error) {
                this.setErrorItems({
                    source: this.$options.name,
                    message: error.message,
                })
            } finally {
                this.loading = false
            }
        },

        // Functions for formatting work orders
        generateRow(dataObj, dates) {
            const row = {}
            for (const date of dates) {
                row[date] = dataObj[date] ?? 0
            }
            return row
        },
        getDaysInRange(start, end) {
            try {
                const days = []
                const current = moment.utc(start).startOf('day')
                const endDay = moment.utc(end).startOf('day')

                while (current.isSameOrBefore(endDay)) {
                    days.push(current.format('YYYY-MM-DD'))
                    current.add(1, 'day')
                }

                return days
            } catch (error) {
                this.setErrorItems({
                    source: this.$options.name,
                    message: error.message,
                })
            }
        },
        // Dates Functions
        selectStartDate(date) {
            this.startDate = date

            if (
                this.endDate &&
                new Date(this.endDate) > new Date(this.maxEndDate)
            ) {
                this.endDate = null
            }
        },
        selectEndDate(date) {
            this.endDate = date
        },
        validateMaxRange(value) {
            if (!value || !this.startDate) return true
            const end = new Date(value)
            const maxRange = new Date(this.startDate)
            maxRange.setMonth(maxRange.getMonth() + 3)
            if (end > maxRange) return 'Maximum range is 3 months'
            if (end > new Date()) return 'End date cannot be in the future'
            return true
        },
        formatDate(seconds) {
            return `${moment.unix(seconds).format('YYYY-MM-DD')}`
        },

        // Dialog
        closeWoReport() {
            this.$emit('closeDailyReport')
        },
    },
}
</script>
